import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Users, CheckCircle2, Clock, Search, Mail, Pencil, DollarSign, Table2, Send, Loader2,
  KeyRound, RefreshCw, UserPlus, Trash2,
} from "lucide-react";
import PortalHeader from "@/components/PortalHeader";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiError } from "@/lib/api";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [signed, setSigned] = useState("all");
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatingSignup, setUpdatingSignup] = useState(null);
  const [assisting, setAssisting] = useState(null);

  const loadStats = useCallback(() => api.get("/admin/stats").then((r) => setStats(r.data)), []);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/customers", {
        params: { search, signed: signed === "all" ? "" : signed === "signed" ? "yes" : "no" },
      });
      setCustomers(data.customers);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, signed]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const t = setTimeout(loadCustomers, 300);
    return () => clearTimeout(t);
  }, [loadCustomers]);

  const emailCustomer = async (c) => {
    try {
      await api.post(`/admin/customers/${c.id}/invite`);
      toast.success(`Invitation sent to ${c.name}.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleSel = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const campaign = async (scope) => {
    try {
      const body = scope === "all" ? { scope: "all" } : { scope: "selected", customer_ids: selected };
      const { data } = await api.post("/admin/email/campaign", body);
      toast.success(data.message);
      setSelected([]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateSignup = async (customer, changes) => {
    setUpdatingSignup(customer.id);
    try {
      const { data } = await api.put(`/admin/customers/${customer.id}/signup`, changes);
      setCustomers((items) => items.map((item) => item.id === customer.id
        ? { ...item, signup: data, effective_price: data.price_total }
        : item));
      toast.success(changes.price_total != null
        ? "Customer portal total updated. The Google Sheet was not changed."
        : `Status changed to ${data.completion_status}.`);
      return data;
    } catch (e) {
      toast.error(apiError(e));
      throw e;
    } finally {
      setUpdatingSignup(null);
    }
  };

  const assistSignup = async () => {
    const customer = assisting;
    if (!customer) return;
    setUpdatingSignup(customer.id);
    try {
      const { data } = await api.post(`/admin/customers/${customer.id}/signup`);
      setCustomers((items) => items.map((item) => item.id === customer.id
        ? { ...item, signed_up: true, signup: data.signup, effective_price: data.signup.price_total }
        : item));
      loadStats();
      if (data.sheet_sync?.status === "error") {
        toast.warning("Customer signed up, but Google Sheets sync failed. Please retry the Sheet sync.");
      } else {
        toast.success("Customer signed up and added to the Google Sheet.");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUpdatingSignup(null);
      setAssisting(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PortalHeader />
      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono-label text-xs text-muted-foreground">Administrator</p>
            <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-primary">Admin Dashboard</h1>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Total Customers" value={stats?.total_customers} tone="primary" />
          <Stat icon={CheckCircle2} label={`Signed Up · ${stats?.season_year || ""}`} value={stats?.signed_up} tone="success" />
          <Stat icon={Clock} label="Not Signed Up" value={stats?.not_signed_up} tone="secondary" />
          <Stat icon={KeyRound} label="Pending Activation" value={stats?.pending_activation} tone="accent" />
        </div>

        <Tabs defaultValue="directory" className="mt-8">
          <TabsList data-testid="admin-tabs" className="h-auto w-full flex-wrap justify-start gap-1">
            <TabsTrigger value="directory"><Users className="mr-1.5 h-4 w-4" /> Directory</TabsTrigger>
            <TabsTrigger value="pricing"><DollarSign className="mr-1.5 h-4 w-4" /> Pricing</TabsTrigger>
            <TabsTrigger value="email"><Mail className="mr-1.5 h-4 w-4" /> Email Campaign</TabsTrigger>
            <TabsTrigger value="sheets"><Table2 className="mr-1.5 h-4 w-4" /> Google Sheets</TabsTrigger>
          </TabsList>

          {/* Directory */}
          <TabsContent value="directory" className="mt-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="customer-search-input" placeholder="Search name, email, phone, RV, location, ID..."
                  value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={signed} onValueChange={setSigned}>
                <SelectTrigger className="w-48" data-testid="signed-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  <SelectItem value="signed">Signed up</SelectItem>
                  <SelectItem value="not">Not signed up</SelectItem>
                </SelectContent>
              </Select>
              {selected.length > 0 && (
                <Button data-testid="email-selected-btn" onClick={() => campaign("selected")} className="rounded-md">
                  <Send className="mr-1.5 h-4 w-4" /> Email {selected.length} selected
                </Button>
              )}
            </div>

            <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full text-sm" data-testid="customer-table">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left">
                    <Th className="w-10"></Th>
                    <Th>Customer</Th>
                    <Th>Contact</Th>
                    <Th>RV / Location</Th>
                    <Th>Price</Th>
                    <Th>Signed Up</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                  )}
                  {!loading && customers.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="no-customers">No customers found.</td></tr>
                  )}
                  {!loading && customers.map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`customer-row-${c.customer_id}`}>
                      <Td><Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggleSel(c.id)} data-testid={`select-${c.customer_id}`} /></Td>
                      <Td>
                        <div className="font-medium text-foreground">{c.name}</div>
                        <div className="font-mono-label text-[10px] text-muted-foreground">{c.customer_id}</div>
                      </Td>
                      <Td>
                        <div>{c.email}</div>
                        <div className="text-muted-foreground">{c.phone}</div>
                      </Td>
                      <Td>
                        <div className="max-w-[200px] truncate">{[c.trailer_make, c.trailer_model].filter(Boolean).join(" ") || "—"}</div>
                        <div className="text-muted-foreground">{[c.resort, c.lot_number && `Lot ${c.lot_number}`].filter(Boolean).join(" · ") || "—"}</div>
                      </Td>
                      <Td>
                        {c.signed_up ? (
                          <EditableSignupTotal customer={c} onSave={(price_total) => updateSignup(c, { price_total })} />
                        ) : (
                          <>
                            <span data-testid={`quoted-total-${c.customer_id}`}>{money(c.effective_price)}</span>
                            {c.specific_price != null && <span className="ml-1 font-mono-label text-[9px] text-secondary">SPECIAL</span>}
                          </>
                        )}
                      </Td>
                      <Td>
                        {c.signed_up ? (
                          <Button size="sm"
                            data-testid={`completion-toggle-${c.customer_id}`}
                            disabled={updatingSignup === c.id}
                            onClick={() => updateSignup(c, {
                              completion_status: c.signup?.completion_status === "Completed" ? "Pending" : "Completed",
                            })}
                            className={c.signup?.completion_status === "Completed"
                              ? "bg-success text-white hover:bg-success/90"
                              : "bg-primary text-primary-foreground hover:bg-primary/90"}>
                            {updatingSignup === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : c.signup?.completion_status === "Completed" ? "Completed" : "Signed Up"}
                          </Button>
                        ) : (
                          <Button size="sm" variant="secondary"
                            data-testid={`signup-toggle-${c.customer_id}`}
                            disabled={updatingSignup === c.id}
                            onClick={() => setAssisting(c)}>
                            {updatingSignup === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Pending"}
                          </Button>
                        )}
                      </Td>
                      <Td className="text-right">
                        <div className="flex justify-end gap-1.5">
                          {!c.signed_up && (
                            <Button size="sm" variant="outline" onClick={() => emailCustomer(c)} data-testid={`email-btn-${c.customer_id}`}>
                              <Mail className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setEditing(c.id)} data-testid={`edit-btn-${c.customer_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="mt-6">
            <PricingPanel onSaved={loadStats} />
          </TabsContent>

          <TabsContent value="email" className="mt-6">
            <div className="max-w-xl rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading text-lg font-semibold text-primary">Winterizing Invitation Campaign</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Send the annual winterizing invitation. Each email includes the customer's name, current pricing
                (customer-specific when applicable), a password setup/reset link if needed, and a signup link.
              </p>
              <div className="mt-5 flex gap-3">
                <Button data-testid="email-all-btn" onClick={() => campaign("all")} className="rounded-full">
                  <Send className="mr-1.5 h-4 w-4" /> Send to All Customers
                </Button>
              </div>
              <p className="mt-4 rounded-lg bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
                Tip: To email specific customers, select them in the Directory tab and use "Email selected".
                Emails are in MOCK mode until a Resend API key is added — content is logged to backend logs.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="sheets" className="mt-6">
            <SheetsPanel seasonYear={stats?.season_year} live={stats?.sheets_live} />
          </TabsContent>
        </Tabs>
      </main>

      {editing && (
        <EditCustomerDialog
          id={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadCustomers(); loadStats(); }}
          onDeleted={() => { setEditing(null); loadCustomers(); loadStats(); }}
        />
      )}
      <AlertDialog open={!!assisting} onOpenChange={(open) => !open && setAssisting(null)}>
        <AlertDialogContent data-testid="admin-assisted-signup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign up {assisting?.name}?</AlertDialogTitle>
            <AlertDialogDescription data-testid="admin-assisted-signup-description">
              This creates their current-season signup using saved service options and adds the customer to the Google Sheet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-assisted-signup-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-assisted-signup-confirm" onClick={assistSignup}>
              <UserPlus className="mr-1.5 h-4 w-4" /> Confirm Signup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <LegalFooter />
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    success: "bg-success/10 text-success",
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent",
  };
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border bg-card p-5">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="mt-3 font-heading text-3xl font-bold text-foreground">{value ?? "—"}</div>
      <div className="font-mono-label text-[10px] text-muted-foreground">{label}</div>
    </motion.div>
  );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 font-mono-label text-[10px] text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;

function PricingPanel({ onSaved }) {
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/pricing", {
        base_price: Number(pricing.base_price),
        slideouts_in_price: Number(pricing.slideouts_in_price),
        add_ons: pricing.add_ons.map((a) => ({ ...a, price: Number(a.price) })),
      });
      setPricing(data);
      onSaved?.();
      toast.success("Pricing updated. Signup pages and emails now use the new prices.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!pricing) return null;
  return (
    <div className="max-w-2xl rounded-xl border border-border bg-card p-6" data-testid="pricing-panel">
      <h3 className="font-heading text-lg font-semibold text-primary">Pricing Administration</h3>
      <p className="mt-1 text-sm text-muted-foreground">No pricing is hardcoded — changes apply everywhere instantly.</p>
      <div className="mt-5 space-y-2">
        <Label>Base Winterizing Price</Label>
        <div className="relative max-w-xs">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <Input data-testid="base-price-input" type="number" step="0.01" className="pl-7"
            value={pricing.base_price} onChange={(e) => setPricing({ ...pricing, base_price: e.target.value })} />
        </div>
      </div>
      <div className="mt-6 space-y-3">
        <Label>Add-On Services</Label>
        {pricing.add_ons.map((a, i) => (
          <div key={a.key} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
            <Input className="flex-1" value={a.label}
              data-testid={`addon-label-${a.key}`}
              onChange={(e) => {
                const next = [...pricing.add_ons]; next[i] = { ...a, label: e.target.value };
                setPricing({ ...pricing, add_ons: next });
              }} />
            <div className="relative w-28">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input type="number" step="0.01" className="pl-6" value={a.price}
                data-testid={`addon-price-${a.key}`}
                onChange={(e) => {
                  const next = [...pricing.add_ons]; next[i] = { ...a, price: e.target.value };
                  setPricing({ ...pricing, add_ons: next });
                }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 space-y-2">
        <Label>Slide-outs In charge</Label>
        <div className="relative max-w-xs">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <Input data-testid="slideouts-price-input" type="number" min="0" step="0.01" className="pl-7"
            value={pricing.slideouts_in_price}
            onChange={(e) => setPricing({ ...pricing, slideouts_in_price: e.target.value })} />
        </div>
      </div>
      <Button data-testid="save-pricing-btn" onClick={save} disabled={saving} className="mt-6 rounded-full">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DollarSign className="mr-2 h-4 w-4" />} Save Pricing
      </Button>
    </div>
  );
}

function SheetsPanel({ seasonYear, live }) {
  const [result, setResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sheets/sync", { season_year: seasonYear });
      setResult(data);
      toast.success(`Synced ${data.row_count} rows to "${data.sheet_title}".`);
    } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="sheets-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-primary">Google Sheets Sync</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            The database stays the master. Each season populates a "Winterizing {seasonYear}" worksheet for staff.
          </p>
        </div>
        <Badge variant={live ? "default" : "secondary"}>{live ? "LIVE" : "MOCK MODE"}</Badge>
      </div>
      <Button data-testid="sheets-sync-btn" onClick={sync} disabled={syncing} className="mt-5 rounded-full">
        {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
        Sync Season to Google Sheets
      </Button>
      {result && (
        <div className="mt-6" data-testid="sheets-result">
          <p className="text-sm font-medium text-foreground">{result.sheet_title} — {result.row_count} rows</p>
          <div className="mt-3 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  {result.columns?.map((c) => <th key={c} className="whitespace-nowrap px-3 py-2 text-left font-mono-label text-[9px] text-muted-foreground">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.rows?.slice(0, 8).map((r, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    {result.columns.map((c) => <td key={c} className="whitespace-nowrap px-3 py-2">{r[c] || "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function EditCustomerDialog({ id, onClose, onSaved, onDeleted }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  useEffect(() => { api.get(`/admin/customers/${id}`).then((r) => setC(r.data)); }, [id]);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/customers/${id}`, {
        name: c.name, email: c.email, phone: c.phone,
        mailing_address: c.mailing_address, city: c.city, state: c.state, zip_code: c.zip_code,
        trailer_make: c.trailer_make, trailer_model: c.trailer_model, resort: c.resort,
        lot_number: c.lot_number, key_location: c.key_location, last_day_at_resort: c.last_day_at_resort,
        notes: c.notes, admin_notes: c.admin_notes,
        specific_price: c.specific_price === "" || c.specific_price == null ? null : Number(c.specific_price),
      });
      toast.success("Customer updated.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const resetPw = async () => {
    try { await api.post(`/admin/customers/${id}/reset-password`); toast.success("Password reset email sent."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteCustomer = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/customers/${id}`);
      toast.success(`${data.message} Google Sheets was left unchanged.`);
      onDeleted();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(false);
      setShowDelete(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="edit-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Customer {c ? `· ${c.customer_id}` : ""}</DialogTitle>
          <DialogDescription data-testid="edit-customer-description">
            Update portal details, manage password access, or permanently delete this customer from the portal.
          </DialogDescription>
        </DialogHeader>
        {!c ? (
          <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <EF label="Name" t="edit-name" v={c.name} on={set("name")} />
              <EF label="Email" t="edit-email" v={c.email} on={set("email")} />
              <EF label="Phone" t="edit-phone" v={c.phone} on={set("phone")} />
              <EF label="Mailing address (street)" t="edit-address" v={c.mailing_address} on={set("mailing_address")} />
              <EF label="City" t="edit-city" v={c.city} on={set("city")} />
              <div className="grid grid-cols-2 gap-3">
                <EF label="State" t="edit-state" v={c.state} on={set("state")} />
                <EF label="Zip" t="edit-zip" v={c.zip_code} on={set("zip_code")} />
              </div>
              <EF label="Trailer make" t="edit-make" v={c.trailer_make} on={set("trailer_make")} />
              <EF label="Trailer model" t="edit-model" v={c.trailer_model} on={set("trailer_model")} />
              <EF label="Resort (trailer location)" t="edit-resort" v={c.resort} on={set("resort")} />
              <EF label="Lot #" t="edit-lot" v={c.lot_number} on={set("lot_number")} />
              <EF label="Key location (hiding spot)" t="edit-key" v={c.key_location} on={set("key_location")} />
              <EF label="Last day at resort" t="edit-lastday" v={c.last_day_at_resort} on={set("last_day_at_resort")} type="date" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Customer-specific price (blank = use base)</Label>
                <Input data-testid="edit-specific-price" type="number" step="0.01"
                  value={c.specific_price ?? ""} onChange={set("specific_price")} />
              </div>
              <div className="space-y-2">
                <Label>Account status</Label>
                <Input value={c.account_status} disabled />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Admin notes (private)</Label>
              <Textarea data-testid="edit-admin-notes" value={c.admin_notes || ""} onChange={(e) => setC({ ...c, admin_notes: e.target.value })} />
            </div>
            {c.history?.length > 0 && (
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <p className="font-mono-label text-[10px] text-muted-foreground">Signup History</p>
                <div className="mt-2 space-y-1 text-sm">
                  {c.history.map((h) => (
                    <div key={h.id} className="flex justify-between">
                      <span>Winterizing {h.season_year}</span>
                      <span className="text-muted-foreground">{money(h.price_total)} · {h.completion_status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="destructive" onClick={() => setShowDelete(true)}
              disabled={!c || deleting} data-testid="delete-customer-btn">
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete Customer
            </Button>
            <Button variant="outline" onClick={resetPw} data-testid="admin-reset-pw-btn">
              <KeyRound className="mr-1.5 h-4 w-4" /> Send Password Reset
            </Button>
          </div>
          <Button onClick={save} disabled={saving || !c} data-testid="save-customer-btn" className="rounded-md">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save Changes
          </Button>
        </DialogFooter>
        <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
          <AlertDialogContent data-testid="delete-customer-confirmation-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {c?.name} from the portal?</AlertDialogTitle>
              <AlertDialogDescription data-testid="delete-customer-warning">
                This permanently deletes the customer account and all portal signup history. Google Sheet rows will remain unchanged.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-customer-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-customer-confirm" onClick={deleteCustomer}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
                Delete Permanently
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

function EF({ label, v, on, t, type = "text" }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input data-testid={t} type={type} value={v || ""} onChange={on} />
    </div>
  );
}

function EditableSignupTotal({ customer, onSave }) {
  const original = Number(customer.signup?.price_total ?? customer.effective_price ?? 0);
  const [value, setValue] = useState(original.toFixed(2));
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(original.toFixed(2)), [original]);

  const save = async () => {
    const next = Number(value);
    if (!Number.isFinite(next) || next < 0) {
      setValue(original.toFixed(2));
      return toast.error("Enter a valid total of zero or more.");
    }
    if (Math.round(next * 100) === Math.round(original * 100)) return;
    setSaving(true);
    try { await onSave(next); } catch { setValue(original.toFixed(2)); } finally { setSaving(false); }
  };

  return (
    <div className="relative w-28">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
      <Input type="number" min="0" step="0.01" value={value} disabled={saving}
        aria-label={`Total for ${customer.name}`} data-testid={`signup-total-${customer.customer_id}`}
        onChange={(e) => setValue(e.target.value)} onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className="h-9 pl-6 pr-2" />
    </div>
  );
}
